/*
 * AdvaHealth Solutions Pty. Ltd. ("AHS") CONFIDENTIAL
 * Copyright (c) 2026 AdvaHealth Solutions Pty. Ltd. All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of AHS. The intellectual and technical concepts contained
 * herein are proprietary to AHS and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from AHS.  Access to the source code contained herein is hereby forbidden to anyone except current AHS employees, managers or contractors who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of AHS. ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */

import { ButtonHTMLAttributes, FC, forwardRef, ReactNode } from "react";
import { Dropdown, DropdownProps } from "react-bootstrap";
import { Badge, BadgeProps } from "./Badge";

export interface DropdownBadgeItem {
  key: string;
  label: ReactNode;
  active?: boolean;
  disabled?: boolean;
}

export interface DropdownBadgeProps
  extends Pick<BadgeProps, "variant" | "size" | "icon" | "label"> {
  id: string;
  items: DropdownBadgeItem[];
  onSelect: (key: string) => void;
  /** Pill shape for the toggle (Dot is not supported for a dropdown) */
  pill?: boolean;
  disabled?: boolean;
  className?: string;
  /** Accessible name when the toggle has no label (icon only) */
  ariaLabel?: string;
  /** e.g. "outside" keeps the menu open while toggling several items */
  autoClose?: DropdownProps["autoClose"];
  align?: DropdownProps["align"];
}

// Dropdown.Toggle has its own `size` prop (Button sizes), so the badge size is passed as `badgeSize`
const BadgeToggle = forwardRef<
  HTMLElement,
  Omit<BadgeProps, "size"> &
    ButtonHTMLAttributes<HTMLButtonElement> & { badgeSize?: BadgeProps["size"] }
>(({ badgeSize, ...props }, ref) => (
  <Badge ref={ref} as="button" type="button" size={badgeSize} {...props} />
));

BadgeToggle.displayName = "BadgeToggle";

/**
 * Design System dropdown badge, e.g. "Show/Hide series" in CP or PP.
 */
export const DropdownBadge: FC<DropdownBadgeProps> = ({
  id,
  items,
  onSelect,
  variant = "primary",
  size = "sm",
  pill = false,
  icon,
  label,
  disabled = false,
  className = "",
  ariaLabel,
  autoClose = true,
  align,
}) => (
  <Dropdown
    className={className}
    autoClose={autoClose}
    align={align}
    onSelect={(eventKey) => {
      if (eventKey) onSelect(eventKey);
    }}
  >
    <Dropdown.Toggle
      id={id}
      as={BadgeToggle}
      className="badge-dropdown"
      variant={variant}
      badgeSize={size}
      shape={pill ? "pill" : "default"}
      icon={icon}
      label={label}
      disabled={disabled || items.length === 0}
      aria-label={ariaLabel}
    />
    <Dropdown.Menu popperConfig={{ strategy: "fixed" }}>
      {items.map((item) => (
        <Dropdown.Item
          key={item.key}
          eventKey={item.key}
          active={item.active}
          disabled={item.disabled}
        >
          {item.label}
        </Dropdown.Item>
      ))}
    </Dropdown.Menu>
  </Dropdown>
);
